import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API_URL } from '../constants/appConstants';
import { useTaskNotifications } from '../contexts/NotificationContext';

// Monday.com task from "My Work Today"
export interface MondayTask {
  id: string;
  name: string;
  boardId: string;
  boardName: string;
  groupName: string | null;
  status: string | null;
  type: string | null;
  dueDate: string | null;
  estimate: string | null; // Original estimate (e.g., "10-12" hours)
  source: 'monday';
  mondayUrl: string;
}

// Mapping from Monday board names to intranet project search terms
// This helps auto-match Monday tasks to the correct intranet project
const BOARD_TO_PROJECT_MAP: Record<string, string> = {
  'Memory Lane - General': 'Memory Lane',
  'Metals Depot - Mobile App & New Platform': 'Metals Depot',
  'Metals Depot - Main Site': 'Metals Depot',
  'Portoll': 'Portoll',
  'Connected Nation': 'Connected Nation',
  'Nikon Live': 'Nikon',
  'Nikon': 'Nikon',
  'J.R. Hoe': 'J.R. Hoe',
  'WordPress Sites': 'WordPress',
  'Internal Tasks': 'eLink Design',
  'Stride Safe': 'Stride Safe',
  'NurseStat': 'NurseStat',
  'Nursestat Board': 'NurseStat',
  'Nursestat Provider App': 'NurseStat',
  'Nursestat - Application Updates': 'NurseStat',
};

interface UseMondayTasksResult {
  tasks: MondayTask[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  forceRefresh: () => Promise<void>;
  getProjectSearchTerm: (boardName: string) => string;
}

/**
 * Hook to fetch Monday.com "My Work Today" tasks
 * @param userId - Optional Monday.com user ID (defaults to current user)
 */
export const useMondayTasks = (userId?: string): UseMondayTasksResult => {
  const [tasks, setTasks] = useState<MondayTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Track previous tasks to detect additions/removals
  const previousTasksRef = useRef<Map<string, MondayTask>>(new Map());
  const isInitialLoadRef = useRef(true);
  
  // Get notification functions
  const { notifyTaskAdded, notifyTaskRemoved } = useTaskNotifications();

  /**
   * Compare new tasks with previous tasks and send notifications
   */
  const detectTaskChanges = useCallback((newTasks: MondayTask[]) => {
    // Skip notifications on initial load
    if (isInitialLoadRef.current) {
      isInitialLoadRef.current = false;
      // Just update the ref with initial tasks
      previousTasksRef.current = new Map(newTasks.map(t => [t.id, t]));
      return;
    }
    
    const newTaskMap = new Map(newTasks.map(t => [t.id, t]));
    const prevTaskMap = previousTasksRef.current;
    
    // Find added tasks (in new but not in previous)
    newTasks.forEach(task => {
      if (!prevTaskMap.has(task.id)) {
        notifyTaskAdded(task.name, task.id);
      }
    });
    
    // Find removed tasks (in previous but not in new)
    Array.from(prevTaskMap.values()).forEach(task => {
      if (!newTaskMap.has(task.id)) {
        notifyTaskRemoved(task.name, task.id);
      }
    });
    
    // Update ref with new tasks
    previousTasksRef.current = newTaskMap;
  }, [notifyTaskAdded, notifyTaskRemoved]);

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const url = userId 
        ? `${API_URL}/monday/my-work/today?userId=${userId}`
        : `${API_URL}/monday/my-work/today`;
      
      const response = await axios.get(url, { withCredentials: true });
      const newTasks = response.data.items || [];
      
      // Detect changes and notify
      detectTaskChanges(newTasks);
      
      setTasks(newTasks);
    } catch (err: any) {
      // Don't show error if Monday integration isn't configured
      if (err.response?.status === 503) {
        setError(null);
        setTasks([]);
      } else {
        setError(err.response?.data?.error || 'Failed to fetch Monday tasks');
      }
    } finally {
      setLoading(false);
    }
  }, [userId, detectTaskChanges]);

  /**
   * Force refresh by clearing the backend cache first, then refetching
   */
  const forceRefresh = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Clear the backend cache first
      await axios.post(`${API_URL}/monday/my-work/clear-cache`, {}, { withCredentials: true });
      
      // Then fetch fresh data
      const url = userId 
        ? `${API_URL}/monday/my-work/today?userId=${userId}`
        : `${API_URL}/monday/my-work/today`;
      
      const response = await axios.get(url, { withCredentials: true });
      const newTasks = response.data.items || [];
      
      // Detect changes and notify
      detectTaskChanges(newTasks);
      
      setTasks(newTasks);
    } catch (err: any) {
      if (err.response?.status === 503) {
        setError(null);
        setTasks([]);
      } else {
        setError(err.response?.data?.error || 'Failed to refresh Monday tasks');
      }
    } finally {
      setLoading(false);
    }
  }, [userId, detectTaskChanges]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  /**
   * Get the search term to use when looking for an intranet project
   * that matches a Monday board
   */
  const getProjectSearchTerm = useCallback((boardName: string): string => {
    return BOARD_TO_PROJECT_MAP[boardName] || boardName;
  }, []);

  return {
    tasks,
    loading,
    error,
    refetch: fetchTasks,
    forceRefresh,
    getProjectSearchTerm
  };
};

/**
 * Hook to fetch Monday.com "My Work Upcoming" tasks (next 7 days)
 * @param userId - Optional Monday.com user ID (defaults to current user)
 */
export const useMondayTasksUpcoming = (userId?: string) => {
  const [tasks, setTasks] = useState<MondayTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const url = userId 
        ? `${API_URL}/monday/my-work/upcoming?userId=${userId}`
        : `${API_URL}/monday/my-work/upcoming`;
      
      const response = await axios.get(url, { withCredentials: true });
      setTasks(response.data.items || []);
    } catch (err: any) {
      if (err.response?.status === 503) {
        setError(null);
        setTasks([]);
      } else {
        setError(err.response?.data?.error || 'Failed to fetch upcoming Monday tasks');
      }
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const getProjectSearchTerm = useCallback((boardName: string): string => {
    return BOARD_TO_PROJECT_MAP[boardName] || boardName;
  }, []);

  return {
    tasks,
    loading,
    error,
    refetch: fetchTasks,
    getProjectSearchTerm
  };
};

export default useMondayTasks;

